import { Component, OnDestroy, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { AiService, AiAnalysisRequest } from 'src/app/_services/ai.service';
import { TranslateService } from '@ngx-translate/core';

interface AnalysisOption {
  key: string;
  label: string;
  icon: string;
  description: string;
}

@Component({
  selector: 'app-ai-analysis-modal',
  templateUrl: './ai-analysis-modal.component.html',
  styleUrls: ['./ai-analysis-modal.component.css']
})
export class AiAnalysisModalComponent implements OnDestroy {
  @Input() filters: any = {};

  selectedOption: AnalysisOption | null = null;
  isStreaming = false;
  isDone = false;
  rawMarkdown = '';
  renderedHtml: SafeHtml = '';

  private streamSub: Subscription;

  readonly options: AnalysisOption[] = [
    {
      key: 'evolution',
      label: 'ai.option.evolution',
      icon: 'las la-chart-line',
      description: 'ai.option.evolutionDesc'
    },
    {
      key: 'personal',
      label: 'ai.option.personal',
      icon: 'las la-user-shield',
      description: 'ai.option.personalDesc'
    },
    {
      key: 'financial',
      label: 'ai.option.financial',
      icon: 'las la-money-bill',
      description: 'ai.option.financialDesc'
    },
    {
      key: 'health',
      label: 'ai.option.health',
      icon: 'las la-heartbeat',
      description: 'ai.option.healthDesc'
    },
    {
      key: 'risk-sends',
      label: 'ai.option.riskSends',
      icon: 'las la-paper-plane',
      description: 'ai.option.riskSendsDesc'
    },
    {
      key: 'out-of-control',
      label: 'ai.option.outOfControl',
      icon: 'las la-exclamation-triangle',
      description: 'ai.option.outOfControlDesc'
    },
    {
      key: 'custom',
      label: 'ai.option.custom',
      icon: 'las la-comment-dots',
      description: 'ai.option.customDesc'
    }
  ];

  customPrompt = '';

  onCustomKeydown(event: KeyboardEvent) {
    if (!event.shiftKey) {
      event.preventDefault();
      if (this.customPrompt.trim()) this.runAnalysis();
    }
  }

  constructor(
    public activeModal: NgbActiveModal,
    private aiService: AiService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {}

  selectOption(option: AnalysisOption) {
    this.selectedOption = option;
    this.rawMarkdown = '';
    this.renderedHtml = '';
    this.isDone = false;
    this.customPrompt = '';
    if (option.key !== 'custom') this.runAnalysis();
  }

  runAnalysis() {
    if (!this.selectedOption) return;

    this.isStreaming = true;
    this.isDone = false;
    this.rawMarkdown = '';
    this.renderedHtml = '';

    const request: AiAnalysisRequest = {
      analysisType: this.selectedOption.key,
      language: this.translate.currentLang || 'es',
      mail: this.filters?.mail,
      dateBegin: this.filters?.dateBegin,
      dateEnd: this.filters?.dateEnd,
      customPrompt: this.selectedOption.key === 'custom' ? this.customPrompt : undefined,
    };

    this.streamSub = this.aiService.streamAnalysis(request).subscribe({
      next: chunk => {
        this.rawMarkdown += chunk;
        this.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(
          this.markdownToHtml(this.rawMarkdown)
        );
      },
      error: err => {
        this.isStreaming = false;
        this.isDone = true;
        this.rawMarkdown += `\n\nError: ${err}`;
        this.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(
          this.markdownToHtml(this.rawMarkdown)
        );
      },
      complete: () => {
        this.isStreaming = false;
        this.isDone = true;
        this.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(
          this.markdownToHtml(this.rawMarkdown)
        );
      }
    });
  }

  exportPdf() {
    if (!this.rawMarkdown) return;
    const optionLabel = this.translate.instant(this.selectedOption.label);
    const cleanedMarkdown = this.rawMarkdown
      .replace(/⏳/g, '')
      .replace(/\*\*Analizando[^\n]*\*\*/g, '')
      .replace(/^\s*\n/gm, '\n')
      .trimStart();
    const content = this.markdownToHtml(cleanedMarkdown);
    const date = new Date().toLocaleDateString('es-ES');

    const printWindow = window.open('', '_blank', 'width=1000,height=700');
    printWindow.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Análisis IA - DLP - ${optionLabel}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 24px; font-size: 12px; color: #222; }
    h1 { color: #1a237e; font-size: 20px; border-bottom: 2px solid #1a237e; padding-bottom: 8px; margin-bottom: 4px; }
    h2 { color: #283593; font-size: 15px; margin-top: 4px; }
    h3, h4, h5 { color: #3949ab; }
    p { color: #444; line-height: 1.6; }
    .meta { color: #888; font-size: 11px; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; margin: 14px 0; page-break-inside: auto; }
    th { background: #1a237e; color: white; padding: 8px 10px; text-align: left; font-weight: 600; border: 1px solid #283593; }
    td { padding: 7px 10px; border: 1px solid #ddd; vertical-align: top; }
    tr:nth-child(even) td { background: #f5f7ff; }
    tr { page-break-inside: avoid; }
    @media print { button { display: none; } }
  </style>
</head>
<body>
  <h1>Análisis IA · DLP</h1>
  <h2>${optionLabel}</h2>
  <p class="meta">Generado el ${date} · Safecloud Pro</p>
  ${content}
  <script>window.onload = function() { window.print(); }<\/script>
</body>
</html>`);
    printWindow.document.close();
  }

  goBack() {
    this.streamSub?.unsubscribe();
    this.selectedOption = null;
    this.isStreaming = false;
    this.isDone = false;
    this.rawMarkdown = '';
    this.renderedHtml = '';
  }

  close() {
    this.streamSub?.unsubscribe();
    this.activeModal.close();
  }

  ngOnDestroy() {
    this.streamSub?.unsubscribe();
  }

  private markdownToHtml(text: string): string {
    if (!text) return '';

    const lines = text.split('\n');
    let html = '';
    let inTable = false;
    let tbodyOpened = false;
    let headerColCount = 0;

    for (const line of lines) {
      const trimmed = line.trim();
      const isTableRow = trimmed.startsWith('|') && trimmed.endsWith('|');
      const isSeparator = isTableRow && /^[\|\-\s:]+$/.test(trimmed);

      if (isTableRow) {
        if (isSeparator) {
          if (!inTable) {
            inTable = true;
            tbodyOpened = true;
            html += '<table class="ai-table"><thead><tr></tr></thead><tbody>';
          } else if (!tbodyOpened) {
            tbodyOpened = true;
            html += '<tbody>';
          }
        } else if (!inTable) {
          inTable = true;
          tbodyOpened = false;
          const cells = trimmed.split('|').filter(c => c.trim() !== '');
          headerColCount = cells.length;
          html += '<table class="ai-table"><thead><tr>';
          for (const c of cells) html += `<th>${this.processInlineMarkdown(c.trim())}</th>`;
          html += '</tr></thead>';
        } else {
          if (!tbodyOpened) {
            tbodyOpened = true;
            html += '<tbody>';
          }
          const cells = trimmed.split('|').filter(c => c.trim() !== '').slice(0, headerColCount);
          html += '<tr>';
          for (let i = 0; i < headerColCount; i++) {
            const c = cells[i] ?? '';
            html += `<td>${this.processInlineMarkdown(c.trim())}</td>`;
          }
          html += '</tr>';
        }
      } else {
        if (inTable) {
          html += '</tbody></table>';
          inTable = false;
          tbodyOpened = false;
        }
        if (/^[-*_]{3,}$/.test(trimmed)) {
          html += '<hr>';
        } else if (trimmed.startsWith('### ')) {
          html += `<h5>${this.escapeHtml(trimmed.substring(4))}</h5>`;
        } else if (trimmed.startsWith('## ')) {
          html += `<h4>${this.escapeHtml(trimmed.substring(3))}</h4>`;
        } else if (trimmed.startsWith('# ')) {
          html += `<h3>${this.escapeHtml(trimmed.substring(2))}</h3>`;
        } else if (trimmed) {
          html += `<p>${this.processInlineMarkdown(trimmed)}</p>`;
        }
      }
    }

    if (inTable) html += '</tbody></table>';
    return html;
  }

  private processInlineMarkdown(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
